import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import type { Observable } from 'rxjs';
import { map } from 'rxjs';
import { environment } from '../../../../environments/environment';
import type { ApiResponse } from '../../domain/entities';
import type {
  GuestbookRepository,
  GuestbookEntry,
  GuestbookAuthResponse,
} from '../../domain/repositories/guestbook.repository';

/**
 * HTTP adapter for {@link GuestbookRepository}.
 *
 * Thin layer on top of HttpClient for guestbook operations. The Authorization
 * header is injected per-request for `createEntry` because it carries a
 * user-scoped access token, not a global app credential.
 */
@Injectable({ providedIn: 'root' })
export class HttpGuestbookRepository implements GuestbookRepository {
  private readonly http = inject(HttpClient);
  private readonly baseUrl = `${environment.apiUrl}/guestbook`;

  public listEntries(): Observable<GuestbookEntry[]> {
    return this.http.get<ApiResponse<GuestbookEntry[]>>(this.baseUrl).pipe(map((res) => res.data));
  }

  public exchangeCode(code: string): Observable<GuestbookAuthResponse> {
    return this.http
      .post<ApiResponse<GuestbookAuthResponse>>(`${this.baseUrl}/auth/callback`, { code })
      .pipe(map((res) => res.data));
  }

  public createEntry(message: string, accessToken: string): Observable<GuestbookEntry> {
    const headers = new HttpHeaders({ Authorization: `Bearer ${accessToken}` });
    return this.http
      .post<ApiResponse<GuestbookEntry>>(this.baseUrl, { message }, { headers })
      .pipe(map((res) => res.data));
  }
}
